// 스킬 트리 그림: 노드 아이콘·가지 문장 이미지 주소와, 이미지가 없을 때 쓰는 인라인 SVG 대체 그림(가지 색 원 위에 얹는 단순 기호).
// 이미지는 public/assets/ui/skilltree/<노드id>.webp, emblem-<가지>.webp (별도 레인에서 생성해 나중에 들어온다). 없어도 화면은 완성된 모양이다.
import { Btn } from '../shared/types';
import type { BranchId, SkillNodeDef } from '../shared/growth';
import { STAT_ICON } from './rpgArt';
import { SKILL_ICONS } from './skillbar';
import { hudUrl, type HudImage } from './dom';
import { assetUrl } from '../pwa/assetMap';

export function nodeIconUrl(id: string): string { return assetUrl('ui/skilltree/' + id + '.webp'); }
export function emblemUrl(branch: BranchId): string { return assetUrl('ui/skilltree/emblem-' + branch + '.webp'); }

/** 액티브 노드는 이미 있는 스킬 버튼 그림을 두 번째 후보로 쓴다(스킬 트리 전용 그림이 없을 때) */
const HUD_ICON: Readonly<Record<string, HudImage>> = { c_dash: 'dash', s_storm: 'storm', g_fall: 'fall', c_crescent: 'crescent' };
export function nodeIconCandidates(id: string): string[] {
  const out = [nodeIconUrl(id)];
  const h = HUD_ICON[id];
  if (h) out.push(hudUrl(h));
  return out;
}

/** 받기에 실패한 주소(다시 요청하지 않는다) */
const MISSING = new Set<string>();

/**
 * 후보 주소를 차례로 시도하는 <img>. 모두 실패하면 이미지를 숨기고 아래에 깔린 대체 그림이 보인다.
 * 이미 실패한 주소는 건너뛰므로 다시 그려도 404를 반복하지 않는다.
 */
export function layeredImg(urls: readonly string[], parent: HTMLElement): HTMLImageElement | null {
  const list = urls.filter((u) => !MISSING.has(u));
  if (list.length === 0) return null;
  const im = document.createElement('img');
  im.alt = '';
  im.draggable = false;
  im.decoding = 'async';
  let i = 0;
  const next = (): void => {
    if (i >= list.length) { im.style.visibility = 'hidden'; return; }
    im.src = list[i++];
  };
  im.addEventListener('error', () => { MISSING.add(list[i - 1]); next(); });
  parent.appendChild(im);
  next();
  return im;
}

// ── 대체 기호(24×24, currentColor) ───────────────────
const svg = (body: string, extra = ''): string => '<svg class="g" viewBox="0 0 24 24" aria-hidden="true" ' + extra + '>' + body + '</svg>';
const GLYPH = {
  shield: svg('<path fill="currentColor" d="M12 2 20 5v6c0 5.2-3.4 9.4-8 11-4.6-1.6-8-5.8-8-11V5z"/><path fill="rgba(0,0,0,.35)" d="M12 5.2 17.4 7.2v4c0 3.6-2.3 6.6-5.4 8z"/>'),
  heartPlus: svg('<path fill="currentColor" d="M12 21s-8.5-5.4-8.5-11.3A4.7 4.7 0 0 1 12 7a4.7 4.7 0 0 1 8.5 2.7C20.5 15.6 12 21 12 21z"/><path fill="#0b0f20" d="M11 10h2v2.2h2.2v2H13V16.4h-2V14.2H8.8v-2H11z"/>'),
  chain: svg('<g fill="none" stroke="currentColor" stroke-width="2.4"><rect x="2.8" y="8.2" width="11" height="7.6" rx="3.8"/><rect x="10.2" y="8.2" width="11" height="7.6" rx="3.8"/></g>'),
  bloom: svg('<g fill="currentColor"><circle cx="12" cy="6" r="3.6"/><circle cx="17.7" cy="10.2" r="3.6"/><circle cx="15.5" cy="17" r="3.6"/><circle cx="8.5" cy="17" r="3.6"/><circle cx="6.3" cy="10.2" r="3.6"/></g><circle cx="12" cy="12.2" r="2.8" fill="#0b0f20"/>'),
  bolt: svg('<path fill="currentColor" d="M13.5 2 4.5 13.6h6L9 22l10.5-12.6h-6.4z"/>'),
  crescent: svg('<path fill="currentColor" d="M20 3C11 3 5 9.6 5 17.4c0 1.4.2 2.7.6 3.6 1.4-5.6 6.2-10 14.4-11.4C21 8.7 21 5.6 20 3z"/>'),
  blade: STAT_ICON.attack.replace('<svg ', '<svg class="g" '),
};
const withClass = (s: string): string => s.replace('<svg ', '<svg class="g" ');

/** 노드 대체 기호 */
export function nodeGlyph(def: SkillNodeDef): string {
  switch (def.effect) {
    case 'skillDamage': {
      const bit = def.skillId === 1 ? Btn.Skill1 : def.skillId === 2 ? Btn.Skill2 : def.skillId === 3 ? Btn.Skill3 : Btn.Skill4;
      return SKILL_ICONS[bit] ? '<span class="gs">' + SKILL_ICONS[bit] + '</span>' : GLYPH.crescent;
    }
    case 'critChance': return withClass(STAT_ICON.critChance);
    case 'skillLifesteal': return GLYPH.heartPlus;
    case 'basicDamage': return GLYPH.blade;
    case 'cooldown': return withClass(STAT_ICON.cooldown);
    case 'linkDamage': return GLYPH.chain;
    case 'maxHp': return withClass(STAT_ICON.maxHp);
    case 'mana': return withClass(STAT_ICON.maxMp);
    case 'damageTaken': return GLYPH.shield;
    case 'bloom': return GLYPH.bloom;
    case 'thunder': return GLYPH.bolt;
    case 'resonance': return SKILL_ICONS[Btn.Awaken] ? '<span class="gs">' + SKILL_ICONS[Btn.Awaken] + '</span>' : GLYPH.shield;
  }
}

/** 가지 문장 대체 기호 */
export function emblemGlyph(branch: BranchId): string {
  if (branch === 'crimson') return svg('<path fill="currentColor" d="M12 1.5 15.2 9 12 12.6 8.8 9zM3.5 5l7 8.6-1.4 6zM20.5 5l-7 8.6 1.4 6zM12 14.2l-2.2 8.3h4.4z"/>');
  if (branch === 'storm') return svg('<g fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 3.2a8.8 8.8 0 1 1-8.8 8.8"/><path d="M12 7.2a4.8 4.8 0 1 1-4.8 4.8"/><path d="M12 11.2a.8.8 0 1 1-.8.8"/></g>');
  return GLYPH.shield;
}

export const LOCK_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" width="12" height="12"><path fill="currentColor" d="M7 10V8a5 5 0 0 1 10 0v2h1.5A1.5 1.5 0 0 1 20 11.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 20.5v-9A1.5 1.5 0 0 1 5.5 10zm2 0h6V8a3 3 0 0 0-6 0z"/></svg>';
export const STAR_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18"><path fill="currentColor" d="m12 1 2.6 8.4L23 12l-8.4 2.6L12 23l-2.6-8.4L1 12l8.4-2.6z"/></svg>';
